/* ============================================================================
   Peanutshell Cart Drawer V1 — behaviour
   - Open/close wiring (header cart icon, PDP ATC, Escape, scrim, X)
   - AJAX cart ops using Shopify's Section Rendering API
   - Upsell resolver fallback (metafield -> recommendations -> bestsellers)
   - Horizontal carousel for "Complete the Set"
   - Dispatches `cart:refresh` (and defensive `cart:update`) on every mutation
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var DRAWER_SELECTOR = '#PeanutCartDrawer';
  var BODY_LOCK_CLASS = 'peanut-cart-drawer-lock';
  // Debug logging is opt-in for production: set window.__PEANUT_CART_DEBUG = true
  // in the console to re-enable.
  var LOG = function () {
    if (!window.__PEANUT_CART_DEBUG) return;
    try { console.log.apply(console, ['[peanut-cart]'].concat([].slice.call(arguments))); } catch (e) {}
  };
  LOG('script loaded', { href: location.href, ts: Date.now() });

  // --------------------------------------------------------------------------
  // Zipify OCU killer. The Zipify cart drawer was baked into the theme via an
  // orphaned app extension. CSS display:none doesn't beat its overlay because
  // Zipify sets inline styles. So we walk the DOM, identify its containers by
  // signature, and rip them out. Also runs as a MutationObserver so re-renders
  // can't resurrect it.
  // --------------------------------------------------------------------------
  function isZipifyElement(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.id === 'PeanutCartDrawer' || el.closest && el.closest('#PeanutCartDrawer')) return false;
    // Signature 1: id or class prefix
    var id = el.id || '';
    if (/^(ocu-|zcd-|zipify-|zcd_|ocu_)/i.test(id)) return true;
    var cls = (typeof el.className === 'string' ? el.className : '') || '';
    if (/\b(ocu-|zcd-|zipify-|z-cart-drawer|ocu_cart)/i.test(cls)) return true;
    // Signature 2: explicit data attr used by the Zipify Vue app
    if (el.hasAttribute && (el.hasAttribute('data-ocu-cart-drawer') || el.hasAttribute('data-zcd'))) return true;
    // Signature 3: Vue scoped attr AND fixed-position viewport-covering div that contains Zipify text
    try {
      if (el.tagName === 'DIV' && el.attributes) {
        var hasVueScope = false;
        for (var i = 0; i < el.attributes.length; i++) {
          if (el.attributes[i].name.indexOf('data-v-') === 0) { hasVueScope = true; break; }
        }
        if (hasVueScope && el.textContent && /Your Cart\s*-|OCU Cart|ocu-cart/i.test(el.textContent.slice(0, 200))) {
          return true;
        }
      }
    } catch (e) {}
    return false;
  }

  function killZipify() {
    try {
      var killed = 0;
      // Direct ID/class matches
      document.querySelectorAll(
        '[id^="ocu-"], [id^="zcd-"], [id^="zipify-"], ' +
        '[class*="ocu-cart"], [class*="zcd-"], [class*="zipify-cart"], ' +
        '[data-ocu-cart-drawer], [data-zcd]'
      ).forEach(function (n) {
        if (n.id === 'PeanutCartDrawer' || (n.closest && n.closest('#PeanutCartDrawer'))) return;
        if (n.id === 'ocu-accelerated-checkout') return; // keep accelerated checkout button
        n.remove();
        killed++;
      });
      // Text-based last-resort: fixed-position divs that contain Zipify drawer text
      document.querySelectorAll('body > div, body > aside, body > section').forEach(function (n) {
        if (n.id === 'PeanutCartDrawer') return;
        if (!n.textContent) return;
        if (/Your Cart\s+-|Calculated at next step/i.test(n.textContent.slice(0, 400))) {
          // Looks like the Zipify cart drawer wrapper.
          var cs = window.getComputedStyle(n);
          if (cs.position === 'fixed' || cs.position === 'absolute') {
            n.remove();
            killed++;
          }
        }
      });
      if (killed) LOG('killZipify removed nodes', killed);
    } catch (e) { LOG('killZipify error', e && e.message); }
  }

  // Run immediately and on DOM mutations.
  killZipify();
  if (typeof MutationObserver === 'function') {
    var zipifyObserver = new MutationObserver(function (mutations) {
      var shouldKill = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          if (isZipifyElement(m.addedNodes[j])) { shouldKill = true; break; }
          // also check if any injected subtree contains Zipify-looking element
          var added = m.addedNodes[j];
          if (added && added.nodeType === 1 && added.querySelector) {
            if (added.querySelector('[id^="ocu-"]:not(#ocu-accelerated-checkout), [id^="zcd-"], [class*="ocu-cart"], [data-ocu-cart-drawer]')) {
              shouldKill = true; break;
            }
          }
        }
        if (shouldKill) break;
      }
      if (shouldKill) killZipify();
    });
    zipifyObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
  // Also re-run a few times after page load for late-loading apps.
  [500, 1500, 3500].forEach(function (ms) { setTimeout(killZipify, ms); });

  // In-flight request guard so rapid clicks don't race.
  var requestInFlight = false;

  // --------------------------------------------------------------------------
  // Cart-icon selector map — Eurus + common theme patterns.
  // Clicking ANY of these opens our drawer and stops the original link.
  // --------------------------------------------------------------------------
  var TRIGGER_SELECTORS = [
    '[data-peanut-cart-open]',
    'a[href$="/cart"]',
    'a[href$="#cart"]',
    '[data-cart-icon]',
    '.header__icon--cart',
    '.js-cart-drawer-open',
    '[data-drawer-trigger="cart"]'
  ].join(',');

  // --------------------------------------------------------------------------
  // Core: drawer open/close
  // --------------------------------------------------------------------------
  function getDrawer() { return document.querySelector(DRAWER_SELECTOR); }

  function openDrawer() {
    var drawer = getDrawer();
    LOG('openDrawer called', { drawerFound: !!drawer });
    if (!drawer) { LOG('ERROR: #PeanutCartDrawer not in DOM'); return; }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    // Belt-and-braces: force inline visibility in case some external CSS overrides ours.
    drawer.style.pointerEvents = 'auto';
    drawer.style.visibility = 'visible';
    drawer.style.zIndex = '999999';
    var panel = drawer.querySelector('.peanut-cart-drawer__panel');
    if (panel) { panel.style.transform = 'translateX(0)'; }
    document.documentElement.classList.add(BODY_LOCK_CLASS);
    document.body.style.overflow = 'hidden';
    // Focus the close button for screen-reader + keyboard users.
    var closer = drawer.querySelector('[data-cart-close]:not(.peanut-cart-drawer__scrim)');
    if (closer) closer.focus({ preventScroll: true });
    reinitRebuyWidgets(drawer);
    LOG('openDrawer done', { isOpen: drawer.classList.contains('is-open'), panelTransform: panel && panel.style.transform });
  }

  function closeDrawer() {
    var drawer = getDrawer();
    LOG('closeDrawer called', { drawerFound: !!drawer });
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.style.pointerEvents = '';
    drawer.style.visibility = '';
    var panel = drawer.querySelector('.peanut-cart-drawer__panel');
    if (panel) { panel.style.transform = ''; }
    document.documentElement.classList.remove(BODY_LOCK_CLASS);
    document.body.style.overflow = '';
  }

  function isOpen() {
    var drawer = getDrawer();
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  // --------------------------------------------------------------------------
  // Event delegation: works on initially rendered AND AJAX-refreshed markup.
  // --------------------------------------------------------------------------
  // Capture-phase listener runs BEFORE any bubble-phase listeners (including
  // Zipify's). stopImmediatePropagation() prevents Zipify's handler from
  // firing at all, so its internal state never desyncs with reality.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(TRIGGER_SELECTORS);
    if (trigger && !trigger.closest(DRAWER_SELECTOR)) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      LOG('cart-icon click intercepted (capture)', { trigger: trigger.outerHTML.slice(0, 120) });
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      openDrawer();
      return;
    }
    // ----- Close via any [data-cart-close] (header X, scrim) ---------------
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      closeDrawer();
      return;
    }
  }, true);

  document.addEventListener('click', function (e) {
    // Redundant guard in bubble phase in case capture-phase listener got removed/overridden.
    var trigger = e.target.closest(TRIGGER_SELECTORS);
    if (trigger && !trigger.closest(DRAWER_SELECTOR)) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      LOG('cart-icon click intercepted (bubble fallback)');
      e.preventDefault();
      openDrawer();
      return;
    }

    // ----- Close ------------------------------------------------------------
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // ----- Remove line ------------------------------------------------------
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      cartChange(removeBtn.dataset.lineKey, 0);
      return;
    }

    // ----- Qty +/- ---------------------------------------------------------
    var qtyDec = e.target.closest('[data-cart-qty-decrease]');
    if (qtyDec) {
      e.preventDefault();
      var curDec = parseInt(qtyDec.dataset.quantity, 10) || 0;
      cartChange(qtyDec.dataset.lineKey, Math.max(0, curDec - 1));
      return;
    }
    var qtyInc = e.target.closest('[data-cart-qty-increase]');
    if (qtyInc) {
      e.preventDefault();
      var curInc = parseInt(qtyInc.dataset.quantity, 10) || 0;
      cartChange(qtyInc.dataset.lineKey, curInc + 1);
      return;
    }

    // ----- Size change on cart line ---------------------------------------
    var sizeBtn = e.target.closest('[data-cart-change-size]');
    if (sizeBtn) {
      e.preventDefault();
      changeLineSize(
        sizeBtn.dataset.lineKey,
        sizeBtn.dataset.variantId,
        parseInt(sizeBtn.dataset.quantity, 10) || 1
      );
      return;
    }

    // ----- "Complete the look" add (in-cart upsell) -----------------------
    var ctsAdd = e.target.closest('[data-cart-cts-add]');
    if (ctsAdd && !ctsAdd.disabled) {
      e.preventDefault();
      cartAdd(ctsAdd.dataset.variantId, 1, ctsAdd);
      return;
    }

    // Rebuy-powered upsell (when configured) handles its own interactions.
  });

  // ESC closes
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Eurus / theme-level cart events. Eurus's own PDP add-to-cart JS
  // (assets/product-cart.js) intercepts the form submit in Alpine's capture
  // phase, so our own submit handler below never fires on PDPs. Instead Eurus
  // dispatches `eurus:cart:items-changed` after a successful add.
  //
  // On any such event we:
  //   1. Re-render our drawer HTML via Shopify's Section Rendering API so the
  //      count, line items, subtotal, and free-shipping banner are fresh.
  //   2. Auto-open the drawer so the shopper sees the item was added.
  //   3. Emit our own `cart:refresh` event for any other theme code that's
  //      listening to our pattern.
  // ─────────────────────────────────────────────────────────────────────────
  var refreshInFlight = false;
  function refreshDrawer(shouldOpen) {
    if (refreshInFlight) return;
    refreshInFlight = true;
    fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data[SECTION_ID]) swapSection(data[SECTION_ID]);
        if (shouldOpen) openDrawer();
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) { dispatchRefresh(cart); })
      .catch(function (err) { LOG('refresh failed', err && err.message); })
      .finally(function () { refreshInFlight = false; });
  }

  // Eurus dispatches `eurus:cart:items-changed` on both adds AND qty changes,
  // so we only refresh from it — never auto-open. Genuine adds are caught by
  // the fetch() hook below and open the drawer there.
  document.addEventListener('eurus:cart:items-changed', function () {
    LOG('eurus:cart:items-changed received');
    refreshDrawer(false);
  });
  // Generic vendor cart events. Some apps dispatch these on page load / cart
  // validation, which is why we never auto-open from them — only refresh.
  ['cart:build', 'cart:refresh', 'cart:updated', 'ajaxCart.afterCartLoad', 'theme:cart:open']
    .forEach(function (name) {
      document.addEventListener(name, function (e) {
        // Don't loop on our own dispatch.
        if (e && e.detail && e.detail.__peanutSelfDispatch) return;
        LOG('cart event received', name);
        refreshDrawer(false);
      });
    });

  // ─────────────────────────────────────────────────────────────────────────
  // fetch() monkey-patch. Eurus's own addToCart handles form submit in Alpine
  // and doesn't reliably dispatch `eurus:cart:items-changed` for every code
  // path. We intercept ALL /cart/add.js, /cart/change.js, /cart/update.js
  // calls (regardless of which code path initiated them) and use the fresh
  // section HTML returned in the SAME response to update our drawer.
  //
  // Why read the response body (not re-fetch /?sections=): Shopify's cart
  // cookie propagation has a small window where a follow-up GET to the
  // section-rendering API can return stale HTML (reflecting cart state from
  // BEFORE the add). The response body from /cart/add.js already contains
  // `sections["cart-drawer"]` with the post-add cart baked in, so using it
  // avoids that race condition entirely.
  // ─────────────────────────────────────────────────────────────────────────
  if (typeof window.fetch === 'function' && !window.__peanutCartFetchPatched) {
    window.__peanutCartFetchPatched = true;
    var origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartAdd = /\/cart\/add(\.js)?($|\?)/.test(url);
      var isCartChange = /\/cart\/(change|update)(\.js)?($|\?)/.test(url);
      // QA 2026-07-23: ask Shopify to bake our fresh section HTML into the
      // mutation response itself. The fallback path (re-fetch /?sections=)
      // races Shopify's cart-cookie propagation and can render STALE drawer
      // HTML — the "quantity didn't update after add" bug. Injecting
      // `sections` here makes every add/change path atomic.
      if ((isCartAdd || isCartChange) && init && init.body) {
        try {
          if (typeof init.body === 'string') {
            var parsed = JSON.parse(init.body);
            if (parsed && typeof parsed === 'object' && !parsed.sections) {
              parsed.sections = SECTION_ID;
              init.body = JSON.stringify(parsed);
            }
          } else if (typeof FormData !== 'undefined' && init.body instanceof FormData && !init.body.has('sections')) {
            init.body.append('sections', SECTION_ID);
          }
        } catch (e) { /* non-JSON body — leave untouched */ }
      }
      var promise = origFetch(input, init);
      if (isCartAdd || isCartChange) {
        promise.then(function (res) {
          if (!res || !res.ok) return;
          if (init && init.headers && (
            (init.headers['X-Peanut-Cart-Internal'] === '1') ||
            (typeof init.headers.get === 'function' && init.headers.get('X-Peanut-Cart-Internal') === '1')
          )) return;
          LOG('fetch hook: cart mutation detected', url);
          // Clone so we don't disturb the original consumer's body stream.
          var clone = res.clone();
          clone.json().then(function (payload) {
            var freshSectionHtml = payload && payload.sections && payload.sections[SECTION_ID];
            if (freshSectionHtml) {
              LOG('fetch hook: using section HTML from add response');
              swapSection(freshSectionHtml);
              // Only genuine adds auto-open the drawer. Qty changes, removes,
              // and app-initiated updates refresh content but keep the drawer
              // in whatever state the user left it (open or closed).
              if (isCartAdd && !isOpen()) openDrawer();
              dispatchRefresh(payload);
            } else {
              LOG('fetch hook: no section in response, falling back to re-fetch');
              setTimeout(function () { refreshDrawer(isCartAdd); }, 120);
            }
          }).catch(function (err) {
            LOG('fetch hook: response parse failed, falling back', err && err.message);
            setTimeout(function () { refreshDrawer(isCartAdd); }, 120);
          });
        }).catch(function () {});
      }
      return promise;
    };
  }

  // Intercept PDP add-to-cart forms so the drawer auto-opens after add.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.action && form.action.indexOf('/cart/add') === -1) return;
    // Only intercept on product forms (Shopify uses action="/cart/add").
    if (!form.matches('form[action*="/cart/add"]')) return;
    // Don't interfere with /cart form submits (those post to /cart, not /cart/add).
    e.preventDefault();
    submitPdpForm(form);
  });

  // --------------------------------------------------------------------------
  // AJAX helpers
  // --------------------------------------------------------------------------
  function setBusy(isBusy) {
    var drawer = getDrawer();
    if (drawer) drawer.dataset.busy = isBusy ? 'true' : 'false';
    requestInFlight = isBusy;
  }

  function fetchJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Peanut-Cart-Internal': '1'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (errJson) {
          var err = new Error(errJson && errJson.message || ('HTTP ' + res.status));
          err.payload = errJson;
          throw err;
        });
      }
      return res.json();
    });
  }

  function swapSection(html) {
    if (!html) return;
    var drawer = getDrawer();
    if (!drawer) return;
    // Section Rendering API returns the full section wrapper. We parse and
    // replace only the inner body so we don't nuke our drawer container's
    // id, classes, or focus.
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var newSection = tmp.querySelector(DRAWER_SELECTOR);
    if (newSection) {
      // Preserve the `is-open` state by swapping children, not the outer node.
      drawer.innerHTML = newSection.innerHTML;
    } else {
      // Fallback: section was returned without our wrapper; just inject.
      drawer.innerHTML = tmp.innerHTML;
    }
    // The innerHTML swap destroys and recreates the Rebuy mount, so re-hydrate.
    reinitRebuyWidgets(drawer);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Header cart-icon badge (QA 2026-07-23): Eurus's own Alpine cart bindings
  // were disabled at install, so nothing updated #cart-icon-bubble. Keep it in
  // sync on every cart mutation and pulse the icon when the count increases.
  // ───────────────────────────────────────────────────────────────────────────
  function updateCartIcon(count) {
    if (typeof count !== 'number' || isNaN(count)) return;
    document.querySelectorAll('#cart-icon-bubble, #mobile-cart-icon-bubble, .count_item').forEach(function (bubble) {
      var prev = parseInt(bubble.getAttribute('data-value'), 10) || 0;
      bubble.setAttribute('data-value', String(count));
      var pill = bubble.firstElementChild;
      if (pill) {
        pill.classList.toggle('hidden', count === 0);
        // Eurus's own JS force-hides the pill via an inline display (its
        // internal cart count is frozen at 0 since we disabled its cart
        // bindings at install). Inline style wins over everything, so set it
        // explicitly ourselves — and pin the pill geometry so it renders as a
        // 20px circle no matter what state Eurus left it in.
        if (count > 0) {
          pill.style.display = 'block';
          pill.style.width = '20px';
          pill.style.height = '20px';
          pill.style.borderRadius = '999px';
        } else {
          pill.style.display = 'none';
        }
        var span = pill.querySelector('span');
        if (span) span.textContent = count < 100 ? String(count) : '99+';
      }
      if (count > prev) {
        var icon = bubble.closest('.header-icon-cart') || bubble;
        icon.classList.remove('peanut-cart-pulse');
        void icon.offsetWidth; // restart the animation
        icon.classList.add('peanut-cart-pulse');
      }
    });
  }

  // Sync the badge on initial load too — the server-rendered pill can carry a
  // stale Eurus inline-hide even when the cart has items.
  function syncCartIconOnLoad() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { updateCartIcon(c.item_count); })
      .catch(function () {});
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCartIconOnLoad);
  } else {
    syncCartIconOnLoad();
  }

  function dispatchRefresh(cart) {
    if (cart && typeof cart.item_count === 'number') {
      updateCartIcon(cart.item_count);
    } else {
      // Payload wasn't a full cart (e.g. /cart/add.js returns the added line).
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { updateCartIcon(c.item_count); })
        .catch(function () {});
    }
    var detail = { cart: cart || null, __peanutSelfDispatch: true };
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: detail }));
    // Defensive: keep Eurus / other theme bits happy.
    document.dispatchEvent(new CustomEvent('cart:update', { detail: detail }));
  }

  function cartAdd(variantId, quantity, sourceEl) {
    if (requestInFlight || !variantId) return;
    setBusy(true);
    // Optimistic UI: flip the ATC to "Added" immediately.
    if (sourceEl) {
      sourceEl.disabled = true;
      sourceEl.setAttribute('aria-disabled', 'true');
      sourceEl.textContent = 'Added';
    }
    fetchJson('/cart/add.js', {
      items: [{ id: parseInt(variantId, 10), quantity: quantity }],
      sections: SECTION_ID,
      sections_url: window.location.pathname
    }).then(function (res) {
      var html = res.sections && res.sections[SECTION_ID];
      swapSection(html);
      if (!isOpen()) openDrawer();
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    }).then(function (cart) {
      dispatchRefresh(cart);
    }).catch(function (err) {
      console.error('[peanut-cart] add failed:', err);
      // Rollback optimistic UI
      if (sourceEl) {
        sourceEl.disabled = false;
        sourceEl.removeAttribute('aria-disabled');
        sourceEl.textContent = 'Add to Cart';
      }
    }).finally(function () { setBusy(false); });
  }

  function cartChange(lineKey, newQty) {
    if (requestInFlight || !lineKey) return;
    setBusy(true);
    fetchJson('/cart/change.js', {
      id: lineKey,
      quantity: newQty,
      sections: SECTION_ID,
      sections_url: window.location.pathname
    }).then(function (res) {
      var html = res.sections && res.sections[SECTION_ID];
      swapSection(html);
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    }).then(function (cart) {
      dispatchRefresh(cart);
    }).catch(function (err) {
      console.error('[peanut-cart] change failed:', err);
    }).finally(function () { setBusy(false); });
  }

  // Size swap on an existing cart line: remove old line, then add new variant.
  function changeLineSize(lineKey, newVariantId, qty) {
    if (requestInFlight || !lineKey || !newVariantId) return;
    setBusy(true);
    fetchJson('/cart/change.js', {
      id: lineKey,
      quantity: 0
    }).then(function () {
      return fetchJson('/cart/add.js', {
        items: [{ id: parseInt(newVariantId, 10), quantity: qty }],
        sections: SECTION_ID,
        sections_url: window.location.pathname
      });
    }).then(function (res) {
      var html = res.sections && res.sections[SECTION_ID];
      swapSection(html);
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    }).then(function (cart) {
      dispatchRefresh(cart);
    }).catch(function (err) {
      console.error('[peanut-cart] size swap failed:', err);
    }).finally(function () { setBusy(false); });
  }

  function submitPdpForm(form) {
    if (requestInFlight) return;
    setBusy(true);
    var formData = new FormData(form);
    // Ensure we ask for our section in the response.
    formData.set('sections', SECTION_ID);
    formData.set('sections_url', window.location.pathname);

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (payload) {
          var err = new Error(payload.message || 'Add to cart failed');
          err.payload = payload;
          throw err;
        });
      }
      return res.json();
    }).then(function (res) {
      var html = res.sections && res.sections[SECTION_ID];
      swapSection(html);
      openDrawer();
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    }).then(function (cart) {
      dispatchRefresh(cart);
    }).catch(function (err) {
      console.error('[peanut-cart] PDP add failed:', err);
      // Fall back to native form submit on failure so the user isn't stranded.
      form.submit();
    }).finally(function () { setBusy(false); });
  }

  // --------------------------------------------------------------------------
  // Rebuy "Complete the Set" widget.
  //
  // The drawer renders a <div data-rebuy-id="..."> mount (see
  // snippets/cart-drawer-upsell.liquid). Rebuy's engine (loaded via the theme
  // app embed) hydrates that node on first paint. But every cart mutation
  // re-renders the drawer through the Section Rendering API, which replaces
  // #PeanutCartDrawer's innerHTML — destroying Rebuy's hydrated DOM and leaving
  // a fresh, empty mount. So after each swap (and on open) we ask Rebuy to
  // re-hydrate. The call is defensive across Rebuy.js versions and is a no-op
  // when Rebuy isn't present (e.g. app embed disabled), so the rest of the
  // drawer keeps working regardless.
  // --------------------------------------------------------------------------
  function reinitRebuyWidgets(root) {
    try {
      var scope = root || document;
      var mounts = scope.querySelectorAll('[data-rebuy-id]');
      if (!mounts.length) return;

      var R = window.Rebuy;
      if (!R) {
        // Rebuy hasn't booted yet; it will hydrate the mount itself once ready.
        return;
      }

      // Preferred: tell the Widgets module to (re)scan the DOM for mounts.
      if (R.Widgets && typeof R.Widgets.hydrate === 'function') { R.Widgets.hydrate(scope); return; }
      if (R.Widgets && typeof R.Widgets.init === 'function') { R.Widgets.init(); return; }
      if (typeof R.loadWidgets === 'function') { R.loadWidgets(); return; }
      if (typeof R.init === 'function') { R.init(); return; }

      // Fallback: re-render any already-tracked widget instances by id.
      if (Array.isArray(R.widgets)) {
        var ids = [];
        mounts.forEach(function (m) {
          var id = m.getAttribute('data-rebuy-id');
          if (id) ids.push(String(id));
        });
        R.widgets.forEach(function (w) {
          if (w && ids.indexOf(String(w.id)) !== -1 && typeof w.render === 'function') {
            try { w.render(); } catch (e) {}
          }
        });
      }
    } catch (e) {
      LOG('reinitRebuyWidgets failed', e && e.message);
    }
  }

  // --------------------------------------------------------------------------
  // Focus trap (lightweight — cycle Tab within the drawer while open)
  // --------------------------------------------------------------------------
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !isOpen()) return;
    var drawer = getDrawer();
    if (!drawer) return;
    var focusables = drawer.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // --------------------------------------------------------------------------
  // First paint: re-hydrate the Rebuy upsell mount if it's already in the DOM.
  // --------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reinitRebuyWidgets(document); });
  } else {
    reinitRebuyWidgets(document);
  }
  // Rebuy may finish booting after our script; re-hydrate when it signals ready.
  document.addEventListener('rebuy:ready', function () { reinitRebuyWidgets(document); });

  // Expose a tiny API for other theme code that wants to programmatically open.
  window.PeanutCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: function () {
      return fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) { swapSection(data[SECTION_ID]); });
    }
  };

  // If the early-boot handler in <head> captured a click before this script
  // loaded, honor it now that we're ready.
  if (window.__PeanutCartPendingOpen) {
    window.__PeanutCartPendingOpen = false;
    openDrawer();
  }
})();
